import _ from 'lodash';
import { DesignModuleNode, DesignObject } from '../../../types';

/**
 * Merge the values of two objects together at the specified @see paths.
 * @param source The source of truth to start with and prefer values from.
 * @param target The existing values to merge into the new version.
 * @param paths The paths to merge the values of @see source and @see target.
 */
export function mergeAdditive<T extends (DesignObject | DesignModuleNode)>(
  source: T,
  target: T,
  paths: string[]
): T {
  const result: T = _.cloneDeep(source);
  paths.forEach((path) => {
    const sourceValue = _.get(source, path) || {};
    const targetValue = _.get(target, path) || {};
    _.set(result, path, mergeValues(sourceValue, targetValue));
  });

  return result;
}

/**
 * Merge two values together. If both values are objects, the result will be a deep merge of the two objects.
 * @param source The source value with changes to merge into @see target.
 * @param target The target value to merge @see source changes into.
 */
function mergeValues<T>(
  source: T,
  target: T
): T {
  if (target == null) {
    return source;
  } else if (source == null) {
    return target;
  }

  // Handle primatives.
  const sourceIsPrimative = typeof source !== 'object';
  const targetIsPrimative = typeof target !== 'object';
  if (sourceIsPrimative && targetIsPrimative) {
    return source;
  } else if (sourceIsPrimative || targetIsPrimative) {
    throw createTypeMismatchError(source, target);
  }

  // Handle arrays.
  const sourceIsArray = Array.isArray(source);
  const targetIsArray = Array.isArray(target);
  if (sourceIsArray || targetIsArray) {
    throw new Error('Unable to merge arrays together.');
  }

  // Handle objects.
  const keys = [...new Set(Object.keys(target).concat(Object.keys(source)))];
  return Object.fromEntries(
    keys.map((key) => {
      const sourceValue = source[key as keyof(T)];
      const targetValue = target[key as keyof(T)];
      return [key, mergeValues(sourceValue, targetValue)];
    })
  ) as T;
}

function createTypeMismatchError<T>(
  source: T,
  target: T
): Error {
  console.error('merge failure', {
    sourceValue: source,
    targetValue: target
  });
  return new Error('Type mismatch when mergeing values.');
}
